import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  createAlarmRule,
  updateAlarmRule,
  CONDITION_TYPES,
  ACTION_TYPES,
  SENSOR_FIELDS, // eslint-disable-line no-unused-vars -- ileride sensör alanları için
} from "../../api/alarmRuleApi";
import {
  getDashboardEdgeDevices,
  getDashboardEdgeDeviceTags,
} from "../../api/dashboardApi";
import { toast } from "react-toastify";

// Helper function to parse actions (could be JSON string or array)
const parseActions = (actions) => {
  if (!actions) return [];
  if (Array.isArray(actions)) return actions;
  try {
    return JSON.parse(actions);
  } catch {
    return [];
  }
};

export default function AlarmRuleForm({ edgeDbId, edges = [], rule = null, onClose }) {
  const { t } = useTranslation();
  const accessToken = useSelector((s) => s.session?.accessToken);
  const isEditing = !!rule;

  const [formData, setFormData] = useState({
    name: rule?.name || "",
    edge_id: rule?.edge_id || edgeDbId,
    field: rule?.field || "temperature",
    device_id: rule?.device_id ?? "",
    tag_name: rule?.tag_name ?? "",
    condition: rule?.condition || "GREATER_THAN",
    threshold: rule?.threshold ?? "",
    threshold2: rule?.threshold2 ?? "",
    actions: parseActions(rule?.actions),
    else_actions: parseActions(rule?.else_actions),
    check_interval_seconds: rule?.check_interval_seconds || 60,
    cooldown_minutes: rule?.cooldown_minutes || 5,
    auto_resolve: rule?.auto_resolve ?? true,
    is_active: rule?.is_active ?? true,
  });

  const [sourceType, setSourceType] = useState(
   "device_tag"  
  );
  const [edgeDevices, setEdgeDevices] = useState([]);
  const [edgeTags, setEdgeTags] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectedEdge = edges.find((e) => e.id === formData.edge_id);
  const edgeIdStr = selectedEdge?.edgeId;

  useEffect(() => {
    if (!accessToken || !edgeIdStr || sourceType !== "device_tag") {
      setEdgeDevices([]);
      return;
    }
    getDashboardEdgeDevices(accessToken, edgeIdStr)
      .then(setEdgeDevices)
      .catch(() => setEdgeDevices([]));
  }, [accessToken, edgeIdStr, sourceType]);

  useEffect(() => {
    if (!accessToken || !edgeIdStr || !formData.device_id || sourceType !== "device_tag") {
      setEdgeTags([]);
      return;
    }
    getDashboardEdgeDeviceTags(accessToken, edgeIdStr, formData.device_id)
      .then(setEdgeTags)
      .catch(() => setEdgeTags([]));
  }, [accessToken, edgeIdStr, formData.device_id, sourceType]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addAction = (isElse = false) => {
    const key = isElse ? "else_actions" : "actions";
    setFormData((prev) => ({
      ...prev,
      [key]: [...prev[key], { type: "EMAIL", target: "" }],
    }));
  };

  const updateAction = (index, field, value, isElse = false) => {
    const key = isElse ? "else_actions" : "actions";
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].map((a, i) =>
        i === index ? { ...a, [field]: value } : a
      ),
    }));
  };

  const removeAction = (index, isElse = false) => {
    const key = isElse ? "else_actions" : "actions";
    setFormData((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceType === "device_tag" && (!formData.device_id || !formData.tag_name)) {
      toast.error(t("alarms.form.needDeviceTag"));
      return;
    }
    setLoading(true);

    try {
      const payload = {
        ...formData,
        threshold: parseFloat(formData.threshold),
        threshold2: formData.condition === "BETWEEN" ? parseFloat(formData.threshold2) : null,
      };
      if (sourceType === "device_tag" && formData.device_id && formData.tag_name) {
        payload.field = null;
        payload.device_id = formData.device_id;
        payload.tag_name = formData.tag_name;
      } else {
        payload.device_id = null;
        payload.tag_name = null;
      }

      if (isEditing) {
        await updateAlarmRule(accessToken, rule.id, payload);
      } else {
        await createAlarmRule(accessToken, payload);
      }
      toast.success(isEditing ? t("alarms.form.updated") : t("alarms.form.created"));
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("alarms.form.genericError"));
    } finally {
      setLoading(false);
    }
  };

  const renderActionList = (actions, isElse = false) => (
    <div style={styles.actionList}>
      {actions.map((action, index) => (
        <div key={index} style={styles.actionItem}>
          <select
            style={styles.select}
            value={action.type}
            onChange={(e) => updateAction(index, "type", e.target.value, isElse)}
          >
            {ACTION_TYPES.map((at) => (
              <option key={at.value} value={at.value}>
                {at.icon} {at.label}
              </option>
            ))}
          </select>

          {(action.type === "SMS" || action.type === "EMAIL") && (
            <input
              style={styles.input}
              type={action.type === "EMAIL" ? "email" : "tel"}
              placeholder={action.type === "EMAIL" ? "email@example.com" : "+905551234567"}
              value={action.target || ""}
              onChange={(e) => updateAction(index, "target", e.target.value, isElse)}
            />
          )}

          {action.type === "CONTROL_DEVICE" && (
            <input
              style={styles.input}
              type="text"
              placeholder={t("alarms.form.hexPlaceholder")}
              value={action.payload || ""}
              onChange={(e) => updateAction(index, "payload", e.target.value, isElse)}
            />
          )}

          <button
            type="button"
            style={styles.removeBtn}
            aria-label={t("alarms.form.removeAction")}
            onClick={() => removeAction(index, isElse)}
          >
            ✕
          </button>
        </div>
      ))}
      <button type="button" style={styles.addActionBtn} onClick={() => addAction(isElse)}>
        {t("alarms.form.addAction")}
      </button>
    </div>
  );

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {isEditing ? t("alarms.form.titleEdit") : t("alarms.form.titleNew")}
          </h3>
          <button style={styles.closeBtn} onClick={onClose} aria-label={t("alarms.common.close")}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>

          {/* Edge Seçimi */}
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("alarms.form.edge")}</label>
            <select
              style={styles.select}
              value={formData.edge_id}
              onChange={(e) => handleChange("edge_id", e.target.value)}
              required
            >
              <option value="">{t("alarms.form.selectEdge")}</option>
              {edges.map((edge) => (
                <option key={edge.id} value={edge.id}>
                  {edge.name ?? edge.edgeId}
                </option>
              ))}
            </select>
          </div>

          {/* Kural Adı */}
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("alarms.form.ruleName")}</label>
            <input
              style={styles.input}
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder={t("alarms.form.ruleNamePh")}
              required
            />
          </div>

          {/* Veri kaynağı: Sabit metrik veya Edge cihaz/tag */}
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("alarms.form.dataSource")}</label>
            <div style={styles.radioRow}>
          
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="sourceType"
                  checked={sourceType === "device_tag"}
                  onChange={() => {
                    setSourceType("device_tag");
                    handleChange("field", "temperature");
                  }}
                />
                {t("alarms.form.sourceDeviceTag")}
              </label>
            </div>
          </div>

     

          {sourceType === "device_tag" && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t("alarms.form.device")}</label>
                <select
                  style={styles.select}
                  value={formData.device_id}
                  onChange={(e) => {
                    handleChange("device_id", e.target.value);
                    handleChange("tag_name", "");
                  }}
                >
                  <option value="">{t("alarms.form.selectDevice")}</option>
                  {edgeDevices.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t("alarms.form.tag")}</label>
                <select
                  style={styles.select}
                  value={formData.tag_name}
                  onChange={(e) => handleChange("tag_name", e.target.value)}
                  disabled={!formData.device_id}
                >
                  <option value="">{t("alarms.form.selectTag")}</option>
                  {edgeTags.map((tg) => (
                    <option key={tg.id} value={tg.name}>
                      {tg.label || tg.name} {tg.unit ? `(${tg.unit})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Koşul */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("alarms.form.condition")}</label>
              <select
                style={styles.select}
                value={formData.condition}
                onChange={(e) => handleChange("condition", e.target.value)}
              >
                {CONDITION_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{t("alarms.form.threshold")}</label>
              <input
                style={styles.input}
                type="number"
                step="0.1"
                value={formData.threshold}
                onChange={(e) => handleChange("threshold", e.target.value)}
                required
              />
            </div>

            {formData.condition === "BETWEEN" && (
              <div style={styles.formGroup}>
                <label style={styles.label}>{t("alarms.form.upper")}</label>
                <input
                  style={styles.input}
                  type="number"
                  step="0.1"
                  value={formData.threshold2}
                  onChange={(e) => handleChange("threshold2", e.target.value)}
                  required
                />
              </div>
            )}
          </div>

          {/* THEN Aksiyonları */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              {t("alarms.form.then")}
            </label>
            {renderActionList(formData.actions, false)}
          </div>

          {/* ELSE Aksiyonları */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              {t("alarms.form.else")}
            </label>
            {renderActionList(formData.else_actions, true)}
          </div>

          {/* Ayarlar */}
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("alarms.form.interval")}</label>
              <input
                style={styles.input}
                type="number"
                min="10"
                max="3600"
                value={formData.check_interval_seconds}
                onChange={(e) => handleChange("check_interval_seconds", parseInt(e.target.value))}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>{t("alarms.form.cooldown")}</label>
              <input
                style={styles.input}
                type="number"
                min="1"
                max="1440"
                value={formData.cooldown_minutes}
                onChange={(e) => handleChange("cooldown_minutes", parseInt(e.target.value))}
              />
            </div>
          </div>

          <div style={styles.checkboxRow}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.auto_resolve}
                onChange={(e) => handleChange("auto_resolve", e.target.checked)}
              />
              {t("alarms.form.autoResolve")}
            </label>

            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleChange("is_active", e.target.checked)}
              />
              {t("alarms.form.active")}
            </label>
          </div>

          {/* Butonlar */}
          <div style={styles.modalFooter}>
            <button type="button" style={styles.cancelBtn} onClick={onClose}>
              {t("alarms.form.cancel")}
            </button>
            <button type="submit" style={styles.submitBtn} disabled={loading}>
              {loading ? t("alarms.form.saving") : isEditing ? t("alarms.form.update") : t("alarms.form.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "var(--bg-card)",
    borderRadius: "12px",
    border: "1px solid var(--border)",
    width: "90%",
    maxWidth: "600px",
    maxHeight: "90vh",
    overflow: "auto",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 20px",
    borderBottom: "1px solid var(--border)",
  },
  modalTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "var(--text-primary)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    color: "var(--text-muted)",
    fontSize: "20px",
    cursor: "pointer",
  },
  form: {
    padding: "20px",
  },
  formGroup: {
    marginBottom: "16px",
  },
  formRow: {
    display: "flex",
    gap: "16px",
    marginBottom: "16px",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "500",
    color: "var(--text-muted)",
    marginBottom: "6px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    backgroundColor: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    color: "var(--text-primary)",
    fontSize: "14px",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    backgroundColor: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    color: "var(--text-primary)",
    fontSize: "14px",
  },
  actionList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  actionItem: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  removeBtn: {
    backgroundColor: "rgba(197, 48, 48, 0.15)",
    color: "var(--danger)",
    border: "none",
    borderRadius: "4px",
    width: "28px",
    height: "28px",
    cursor: "pointer",
  },
  addActionBtn: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-muted)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "13px",
    cursor: "pointer",
    marginTop: "8px",
  },
  radioRow: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--text-primary)",
    fontSize: "14px",
    cursor: "pointer",
  },
  checkboxRow: {
    display: "flex",
    gap: "24px",
    marginBottom: "16px",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--text-primary)",
    fontSize: "14px",
    cursor: "pointer",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    paddingTop: "16px",
    borderTop: "1px solid var(--border)",
  },
  cancelBtn: {
    backgroundColor: "var(--bg-hover)",
    color: "var(--text-primary)",
    border: "1px solid var(--border)",
    borderRadius: "6px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
  },
  submitBtn: {
    backgroundColor: "var(--btn-primary)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "10px 20px",
    fontSize: "14px",
    cursor: "pointer",
  },
};
